import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Button,
    Alert,
    CircularProgress
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { customUserDataFieldService } from '../../services/customUserDataFieldService';
import { UserDataFieldDto, BusinessDataFieldDto, BusinessDataFieldType } from '../../types/customUserDataFields';
import CustomFieldDialog from './CustomFieldDialog';

const CustomDataTab: React.FC = () => {
    const [fields, setFields] = useState<UserDataFieldDto[]>([]);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [editingField, setEditingField] = useState<UserDataFieldDto | null>(null);

    useEffect(() => {
        loadFields();
    }, []);

    const loadFields = async () => {
        try {
            setIsLoading(true);
            setError('');

            // Fetch definitions from BusinessDataFieldsController and values from UserDataFieldsController
            const [businessFields, userFields] = await Promise.all([
                customUserDataFieldService.getBusinessDataFields(),
                customUserDataFieldService.getCurrentUserFields()
            ]);

            // Create a map of existing user values for quick lookup
            const userFieldMap = new Map(userFields.map((f: UserDataFieldDto) => [f.businessDataFieldId, f]));

            // Merge definitions with values
            const mergedFields: UserDataFieldDto[] = businessFields.map((bField: BusinessDataFieldDto) => {
                const existingField = userFieldMap.get(bField.id);
                if (existingField) {
                    return existingField;
                }

                // Create a placeholder for fields that haven't been set by this user yet
                return {
                    id: '', // Placeholder ID, will be generated by backend on save
                    userId: '', // Placeholder, not needed for display
                    businessDataFieldId: bField.id,
                    value: '',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    businessDataField: bField
                };
            });

            setFields(mergedFields);
        } catch (err) {
            console.error('Error loading custom fields:', err);
            setError('Failed to load custom fields');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditField = (field: UserDataFieldDto) => {
        setEditingField(field);
    };

    const handleSaveField = async (fieldId: string, value: string) => {
        try {
            await customUserDataFieldService.updateUserField(fieldId, { value });
            setEditingField(null);
            // Reload fields to get updated data
            await loadFields();
        } catch (err) {
            console.error('Error updating field:', err);
            setError('Failed to update field');
        }
    };

    const handleCloseDialog = () => {
        setEditingField(null);
    };

    const getFieldDisplayValue = (field: UserDataFieldDto): string => {
        const value = field.value?.trim();
        if (!value) return 'Not set';

        switch (field.businessDataField.type) {
            case BusinessDataFieldType.Bool:
                return value === 'true' ? 'Yes' : 'No';
            case BusinessDataFieldType.Text:
            case BusinessDataFieldType.MultilineText:
                return value.length > 50 ? `${value.substring(0, 50)}...` : value;
            default:
                return value;
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                User Data Fields
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {fields.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No user data fields have been configured for your business.
                </Typography>
            ) : (
                <List>
                    {fields.map((field, index) => (
                        <React.Fragment key={field.businessDataFieldId}>
                            <ListItem
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                    '&:hover': {
                                        backgroundColor: 'action.hover',
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <Typography variant="subtitle2" component="div">
                                                {field.businessDataField.name}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            {field.businessDataField.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                    {field.businessDataField.description}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                                                Value:
                                            </Typography>
                                            <Typography variant="body1">
                                                {getFieldDisplayValue(field)}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleEditField(field)}
                                >
                                    Edit
                                </Button>
                            </ListItem>
                        </React.Fragment>
                    ))}
                </List>
            )}

            {editingField && (
                <CustomFieldDialog
                    field={editingField}
                    open={!!editingField}
                    onClose={handleCloseDialog}
                    onSave={handleSaveField}
                />
            )}
        </Box>
    );
};

export default CustomDataTab;
